// frontend/src/services/IAPService.js
// JavaScript wrapper around the native StoreKitPlugin (local Capacitor plugin).
// All methods are no-ops on web — callers should guard with Capacitor.isNativePlatform().

import { registerPlugin, Capacitor } from '@capacitor/core';

// Registers the JS <-> native bridge for our local StoreKitPlugin.
// On web this resolves to a stub that throws, so always check isNativePlatform() first.
const StoreKit = registerPlugin('StoreKit');

// Product ID → human-readable tier name
export const PRODUCT_IDS = {
  STANDARD: 'com.squeakbase.app.standard.1',
  LAB: 'com.squeakbase.app.lab.1',
};

export const TIER_FOR_PRODUCT = {
  [PRODUCT_IDS.STANDARD]: 'standard',
  [PRODUCT_IDS.LAB]: 'lab',
};

const isNative = () => Capacitor.isNativePlatform();

/**
 * Fetch available StoreKit products from the App Store.
 * Returns an array of product objects: { productId, localizedTitle, localizedDescription, displayPrice }
 */
export async function getProducts() {
  if (!isNative()) return [];
  return StoreKit.getProducts({ productIds: Object.values(PRODUCT_IDS) });
}

/**
 * Initiate a purchase for the given productId.
 * Resolves to { originalTransactionId, signedTransaction } on success.
 * Rejects with { code, message } on failure/cancellation.
 */
export async function purchase(productId) {
  if (!isNative()) throw new Error('IAP is only available on iOS');
  return StoreKit.purchase({ productId });
}

/**
 * Restore previous purchases (calls AppStore.sync()).
 * Resolves to an array of active entitlements:
 *   [{ productId, originalTransactionId, signedTransaction }, ...]
 */
export async function restorePurchases() {
  if (!isNative()) return [];
  return StoreKit.restorePurchases();
}

/**
 * Check Transaction.currentEntitlements for active subscriptions.
 * Returns an array of active entitlements:
 *   [{ productId, originalTransactionId, signedTransaction, expiresDate }, ...]
 */
export async function getCurrentEntitlements() {
  if (!isNative()) return [];
  return StoreKit.getCurrentEntitlements();
}
